import React, { Fragment, useEffect, useState } from 'react'
import { Dialog, Menu, Transition } from '@headlessui/react'
import { CheckIcon, ChevronDownIcon, PlusIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { createTeam, getMyTeams } from '../../Requests/TeamRequests'
import { getTeamUuid, setSecureCookie } from '../../utils/cookies'

function TeamSwitcher() {
    const { t } = useTranslation()
    const [teams, setTeams] = useState(null)
    const [creating, setCreating] = useState(false)
    const [createOpen, setCreateOpen] = useState(false)
    const [newName, setNewName] = useState("")
    const current_uuid = getTeamUuid()

    useEffect(() => {
        getMyTeams().then((res) => {
            setTeams(res.data)
        }).catch(() => {
            setTeams([])
        })
    }, [])

    // The whole app reads the active team from the cookie, so switching reloads
    // into the dashboard rather than leaving half-loaded data from the old team.
    const switchTo = (team) => {
        if (team.uuid === current_uuid) return
        setSecureCookie("team_uuid", team.uuid)
        // Keeps the sidebar from flashing the previous team's balance.
        setSecureCookie("credits", parseFloat(team.credits || 0))
        window.location.assign("/dashboard")
    }

    const handleCreate = (event) => {
        event.preventDefault()
        if (!newName.trim() || creating) return
        setCreating(true)
        createTeam(newName.trim()).then((res) => {
            switchTo(res.data)
        }).catch(() => {
            setCreating(false)
        })
    }

    const currentTeam = (teams || []).find((team) => team.uuid === current_uuid)

    return (
        <Menu as="div" className="relative px-3 pb-2">
            <Menu.Button className="flex w-full items-center justify-between gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-semibold text-gray-900 hover:bg-gray-50">
                <span className="flex min-w-0 items-center gap-2">
                    <UserGroupIcon className="h-4 w-4 shrink-0 text-gray-500" />
                    <span className="truncate">{currentTeam ? currentTeam.name : t('Sidebar.TeamSwitcherLoading')}</span>
                </span>
                <ChevronDownIcon className="h-4 w-4 shrink-0 text-gray-500" />
            </Menu.Button>

            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <Menu.Items className="absolute left-3 right-3 z-20 mt-1 origin-top rounded-lg border border-gray-200 bg-white py-1 shadow-lg focus:outline-none">
                    {(teams || []).map((team) => (
                        <Menu.Item key={team.uuid}>
                            {({ active }) => (
                                <button
                                    type="button"
                                    onClick={() => switchTo(team)}
                                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm ${active ? 'bg-gray-50' : ''}`}
                                >
                                    <span className="flex min-w-0 items-center gap-2">
                                        <UserGroupIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                        <span className="truncate">{team.name}</span>
                                    </span>
                                    {team.uuid === current_uuid && <CheckIcon className="h-4 w-4 shrink-0 text-gray-900" />}
                                </button>
                            )}
                        </Menu.Item>
                    ))}

                    <div className="my-1 border-t border-gray-100" />

                    <Menu.Item>
                        {({ active }) => (
                            <button
                                type="button"
                                onClick={() => setCreateOpen(true)}
                                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${active ? 'bg-gray-50' : ''}`}
                            >
                                <PlusIcon className="h-4 w-4 shrink-0 text-gray-500" />
                                <span className="truncate">{t('Sidebar.CreateTeam')}</span>
                            </button>
                        )}
                    </Menu.Item>
                </Menu.Items>
            </Transition>

            <Transition.Root show={createOpen} as={Fragment}>
                <Dialog as="div" className="relative z-30" onClose={() => setCreateOpen(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-30 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Dialog.Panel className="w-full max-w-md transform rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                                <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                                    {t('Sidebar.CreateTeam')}
                                </Dialog.Title>
                                <form onSubmit={handleCreate} className="mt-4 flex flex-col gap-4">
                                    <input
                                        type="text"
                                        autoFocus
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        placeholder={t('Sidebar.CreateTeamPlaceholder')}
                                        className="w-full rounded-lg border-gray-300 text-sm focus:border-black focus:ring-black"
                                    />
                                    <div className="flex justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setCreateOpen(false)}
                                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50"
                                        >
                                            {t('Sidebar.CreateTeamCancel')}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={creating || !newName.trim()}
                                            className="rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 disabled:opacity-50"
                                        >
                                            {t('Sidebar.CreateTeamConfirm')}
                                        </button>
                                    </div>
                                </form>
                            </Dialog.Panel>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </Menu>
    )
}

export default TeamSwitcher
